import * as THREE from 'three';
import { Loop } from './core/Loop';
import { TimeControl } from './core/Time';
import { BodyView } from './physics/BodyView';
import { createWorld, initPhysics } from './physics/World';
import { Stage } from './view/Stage';

/**
 * Phase 0 스모크.
 *
 * 아직 게임이 아니다. 고정 timestep 루프 → Rapier 물리 → three.js 렌더 →
 * 보간까지 파이프라인이 실제로 이어져 있는지 확인하는 것이 목적이다.
 * 여기 있는 상자는 캐릭터 placeholder가 아니라 배관 점검용이며,
 * Phase 1에서 고양이 GLB로 교체된다.
 */
async function bootstrap(): Promise<void> {
  const canvas = document.querySelector<HTMLCanvasElement>('#stage');
  const status = document.querySelector<HTMLElement>('#status');
  if (!canvas || !status) {
    throw new Error('필수 DOM 요소를 찾지 못했다: #stage, #status');
  }

  status.textContent = '물리 모듈 불러오는 중…';
  const rapier = await initPhysics();

  const stage = new Stage(canvas);
  const world = createWorld(rapier);
  const time = new TimeControl();
  const views: BodyView[] = [];

  // 바닥
  const groundSize = { x: 12, y: 0.5, z: 12 };
  const groundBody = world.createRigidBody(rapier.RigidBodyDesc.fixed().setTranslation(0, -groundSize.y / 2, 0));
  world.createCollider(
    rapier.ColliderDesc.cuboid(groundSize.x / 2, groundSize.y / 2, groundSize.z / 2).setRestitution(0.2),
    groundBody,
  );
  const groundMesh = new THREE.Mesh(
    new THREE.BoxGeometry(groundSize.x, groundSize.y, groundSize.z),
    new THREE.MeshStandardMaterial({ color: 0xf2e4cf, roughness: 0.95 }),
  );
  groundMesh.receiveShadow = true;
  stage.scene.add(groundMesh);
  views.push(new BodyView(groundBody, groundMesh));

  // 낙하 상자
  const startPosition = { x: 0, y: 6.5, z: 0 };
  const boxBody = world.createRigidBody(
    rapier.RigidBodyDesc.dynamic()
      .setTranslation(startPosition.x, startPosition.y, startPosition.z)
      .setAngularDamping(0.2),
  );
  world.createCollider(rapier.ColliderDesc.cuboid(0.5, 0.5, 0.5).setRestitution(0.45).setFriction(0.8), boxBody);
  const boxMesh = new THREE.Mesh(
    new THREE.BoxGeometry(1, 1, 1),
    new THREE.MeshStandardMaterial({ color: 0xffb454, roughness: 0.4, metalness: 0.05 }),
  );
  boxMesh.castShadow = true;
  stage.scene.add(boxMesh);
  const boxView = new BodyView(boxBody, boxMesh);
  views.push(boxView);

  let stepCount = 0;

  const loop = new Loop(
    {
      fixedStep: () => {
        world.step();
        stepCount += 1;
        for (const view of views) view.sync();
      },
      render: (alpha) => {
        for (const view of views) view.apply(alpha);
        stage.render();
      },
    },
    time,
  );

  /** 상자를 초기 위치로 되돌린다. 입력·시간제어·물리가 함께 도는지 확인하는 용도다. */
  const respawn = (): void => {
    // physics-exempt: 리스폰은 시뮬레이션 결과가 아니라 상태 초기화이므로 직접 대입이 정당하다.
    // eslint-disable-next-line physics/no-state-override
    boxBody.setTranslation(startPosition, true);
    // physics-exempt: 위와 같음. 초기 자세를 고정해야 매 시도가 같은 조건에서 시작한다.
    // eslint-disable-next-line physics/no-state-override
    boxBody.setRotation({ x: 0, y: 0, z: 0, w: 1 }, true);
    // physics-exempt: 이전 시도의 잔여 운동량을 지운다.
    // eslint-disable-next-line physics/no-state-override
    boxBody.setLinvel({ x: 0, y: 0, z: 0 }, true);
    // physics-exempt: 위와 같음.
    // eslint-disable-next-line physics/no-state-override
    boxBody.setAngvel({ x: (Math.random() - 0.5) * 6, y: 0, z: (Math.random() - 0.5) * 6 }, true);

    boxView.snap();
    time.reset();
    time.hitStop(0.04);
  };

  canvas.addEventListener('pointerdown', respawn);

  loop.start();

  // 상태 표시는 매 프레임 갱신할 필요가 없다. DOM 갱신 비용만 든다.
  window.setInterval(() => {
    const y = boxBody.translation().y.toFixed(2);
    status.textContent = `${loop.fps.toFixed(0)} fps · ${stepCount} steps · y ${y} · 화면을 누르면 다시 떨어진다`;
  }, 250);
}

bootstrap().catch((error: unknown) => {
  const status = document.querySelector<HTMLElement>('#status');
  const message = error instanceof Error ? error.message : String(error);
  if (status) status.textContent = `초기화 실패: ${message}`;
  console.error(error);
});
